/**
 * Error Handling Hook
 * Provides error handling capabilities to React components
 * Implements Requirements: 2.4, 3.1, 7.4
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  errorHandlingService, 
  ErrorType, 
  ErrorSeverity, 
  GameError, 
  ErrorContext,
  ErrorHandlingOptions 
} from '../services/ErrorHandlingService';
import { 
  networkMonitorService, 
  NetworkState, 
  NetworkRetryOptions 
} from '../services/NetworkMonitorService';

export interface ErrorHandlingState {
  currentError: GameError | null;
  errorHistory: GameError[];
  isOnline: boolean;
  networkState: NetworkState;
  hasRecoverableError: boolean;
}

export interface ErrorHandlingActions {
  handleError: (
    error: Error | string,
    type: ErrorType,
    severity?: ErrorSeverity,
    context?: ErrorContext,
    options?: ErrorHandlingOptions
  ) => GameError;
  handleNetworkError: (error: Error | string, context?: ErrorContext) => GameError;
  handleMultiplayerError: (error: Error | string, context?: ErrorContext) => GameError;
  handleAuthError: (error: Error | string, context?: ErrorContext) => GameError;
  handleStorageError: (error: Error | string, context?: ErrorContext) => GameError;
  handleValidationError: (error: Error | string, context?: ErrorContext) => GameError;
  performNetworkRequest: <T>(
    requestFn: () => Promise<T>,
    options?: NetworkRetryOptions,
    context?: { action?: string; component?: string }
  ) => Promise<T>;
  clearCurrentError: () => void;
  clearErrorHistory: () => void;
  retryLastAction: () => void;
  testConnectivity: () => Promise<boolean>;
}

export function useErrorHandling(componentName?: string): [ErrorHandlingState, ErrorHandlingActions] {
  const [currentError, setCurrentError] = useState<GameError | null>(null);
  const [errorHistory, setErrorHistory] = useState<GameError[]>([]);
  const [networkState, setNetworkState] = useState<NetworkState>(
    networkMonitorService.getNetworkState()
  );

  // Subscribe to error events
  useEffect(() => {
    const unsubscribeErrors = errorHandlingService.subscribe((error) => {
      setCurrentError(error);
      setErrorHistory(prev => [error, ...prev.slice(0, 9)]); // Keep last 10 errors
    });

    const unsubscribeNetwork = networkMonitorService.subscribe((state) => {
      setNetworkState(state);
    });

    return () => {
      unsubscribeErrors();
      unsubscribeNetwork();
    };
  }, []);

  // Create context with component name
  const createContext = useCallback((additionalContext?: ErrorContext): ErrorContext => {
    return {
      component: componentName,
      ...additionalContext
    };
  }, [componentName]);

  // Error handling actions
  const handleError = useCallback((
    error: Error | string,
    type: ErrorType,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    context?: ErrorContext,
    options?: ErrorHandlingOptions
  ) => {
    return errorHandlingService.handleError(
      error,
      type,
      severity,
      createContext(context),
      options
    );
  }, [createContext]);

  const handleNetworkError = useCallback((error: Error | string, context?: ErrorContext) => {
    return errorHandlingService.handleNetworkError(error, createContext(context));
  }, [createContext]);

  const handleMultiplayerError = useCallback((error: Error | string, context?: ErrorContext) => {
    return errorHandlingService.handleMultiplayerError(error, createContext(context));
  }, [createContext]);

  const handleAuthError = useCallback((error: Error | string, context?: ErrorContext) => {
    return errorHandlingService.handleAuthenticationError(error, createContext(context));
  }, [createContext]);

  const handleStorageError = useCallback((error: Error | string, context?: ErrorContext) => {
    return errorHandlingService.handleStorageError(error, createContext(context));
  }, [createContext]);

  const handleValidationError = useCallback((error: Error | string, context?: ErrorContext) => {
    return errorHandlingService.handleValidationError(error, createContext(context));
  }, [createContext]);

  const performNetworkRequest = useCallback(<T>(
    requestFn: () => Promise<T>,
    options?: NetworkRetryOptions,
    context?: { action?: string; component?: string }
  ) => {
    const finalContext = {
      component: componentName,
      ...context
    };
    return networkMonitorService.performNetworkRequest(requestFn, options, finalContext);
  }, [componentName]);

  const clearCurrentError = useCallback(() => {
    setCurrentError(null);
  }, []);

  const clearErrorHistory = useCallback(() => {
    setErrorHistory([]);
    errorHandlingService.clearErrorHistory();
  }, []);

  const retryLastAction = useCallback(() => {
    if (currentError && currentError.recoveryActions && currentError.recoveryActions.length > 0) {
      const primaryAction = currentError.recoveryActions.find(action => action.primary) ||
                           currentError.recoveryActions[0];
      
      if (primaryAction) {
        primaryAction.action();
        setCurrentError(null);
      }
    }
  }, [currentError]);

  const testConnectivity = useCallback(() => {
    return networkMonitorService.testConnectivity();
  }, []);

  // Computed state
  const hasRecoverableError = currentError ? errorHandlingService.isRecoverable(currentError) : false;

  const state: ErrorHandlingState = {
    currentError,
    errorHistory,
    isOnline: networkState.isOnline,
    networkState,
    hasRecoverableError
  };

  const actions: ErrorHandlingActions = {
    handleError,
    handleNetworkError,
    handleMultiplayerError,
    handleAuthError,
    handleStorageError,
    handleValidationError,
    performNetworkRequest,
    clearCurrentError,
    clearErrorHistory,
    retryLastAction,
    testConnectivity
  };

  return [state, actions];
}

/**
 * Hook for handling async operations with automatic error handling
 */
export function useAsyncOperation<T>(
  operation: () => Promise<T>,
  dependencies: any[] = [],
  options: {
    errorType?: ErrorType;
    errorSeverity?: ErrorSeverity;
    context?: ErrorContext;
    retryOptions?: NetworkRetryOptions;
    componentName?: string;
  } = {}
) {
  const [state, setState] = useState<{
    data: T | null;
    loading: boolean;
    error: GameError | null;
  }>({
    data: null,
    loading: false,
    error: null
  });

  const [, { handleError, performNetworkRequest }] = useErrorHandling(options.componentName);

  const execute = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      let result: T;
      
      if (options.retryOptions) {
        // Use network request with retry logic
        result = await performNetworkRequest(
          operation,
          options.retryOptions,
          { 
            action: 'async_operation',
            component: options.componentName 
          }
        );
      } else {
        // Direct operation
        result = await operation();
      }

      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error) {
      const gameError = handleError(
        error instanceof Error ? error : new Error(String(error)),
        options.errorType || ErrorType.SYSTEM,
        options.errorSeverity || ErrorSeverity.MEDIUM,
        options.context
      );

      setState({ data: null, loading: false, error: gameError });
      throw error;
    }
  }, [operation, handleError, performNetworkRequest, options, ...dependencies]);

  const retry = useCallback(() => {
    return execute();
  }, [execute]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    retry,
    reset
  };
}

/**
 * Hook for handling form validation errors
 */
export function useFormErrorHandling(componentName?: string) {
  const [, { handleValidationError }] = useErrorHandling(componentName);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const setFieldError = useCallback((field: string, error: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    handleValidationError(error, { 
      additionalData: { field, formComponent: componentName } 
    });
  }, [handleValidationError, componentName]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  return {
    fieldErrors,
    hasErrors,
    setFieldError,
    clearFieldError,
    clearAllErrors
  };
}